import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { postData, getFile } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

const REQUIRED_COLS = ["vendor_name", "account_group", "company_code"];
const ALL_COLS = [
  "vendor_name", "vendor_type", "account_group", "company_code", "vendor_category",
  "street", "city", "state", "pin_code", "country_code",
  "contact_name", "contact_phone", "contact_email",
  "pan", "gstin", "msme_reg",
  "bank_name", "bank_account_number", "ifsc_code", "payment_terms",
];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) => {
    const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
    const obj = {};
    headers.forEach((h, i) => { if (vals[i]) obj[h] = vals[i]; });
    return obj;
  });
  return { headers, rows };
}

export default function BulkUploadModal({ onClose, onDone }) {
  const toast = useToast();
  const [step, setStep]       = useState("upload");  // upload | preview | result
  const [rows, setRows]       = useState([]);
  const [headers, setHeaders] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError]     = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  // BUG-25: dedup preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview]               = useState(null); // { total, blocking, warning, can_import, rows: [...] }
  const fileRef = useRef();

  const runPreview = async (parsedRows) => {
    setPreviewLoading(true);
    setPreview(null);
    const res = await postData("t2t/vendors/bulk-import/preview", { rows: parsedRows });
    setPreviewLoading(false);
    if (res.error) {
      // Soft-fail: still show the preview table with raw rows. Admin can attempt import,
      // backend will re-run dedup and reject exact matches there too.
      toast(`Dedup preview failed: ${res.error.message || "unknown"}. Import will still re-check on submit.`, "error");
      return;
    }
    setPreview(res.data?.data || res.data || null);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows: r } = parseCSV(ev.target.result);
      const missing = REQUIRED_COLS.filter((c) => !h.includes(c));
      if (missing.length > 0) {
        setError(`Missing required columns: ${missing.join(", ")}`);
        setRows([]);
        return;
      }
      if (r.length === 0) { setError("File has no data rows."); return; }
      if (r.length > 200) { setError("Maximum 200 rows per import."); return; }
      setHeaders(h);
      setRows(r);
      setStep("preview");
      runPreview(r);
    };
    reader.readAsText(file);
  };

  // BUG-23: was using getData() which always runs response.json() — backend
  // sends raw text/csv, so JSON parse threw and the modal silently swallowed
  // the error. Switched to getFile() (returns raw Response), then read .text().
  const downloadTemplate = async () => {
    const res = await getFile("t2t/vendors/bulk-import/template");
    if (res.error || !res.data) {
      toast(res.error?.message || "Couldn't download the CSV template. Please try again.", "error");
      return;
    }
    try {
      const csvText = await res.data.text();
      if (!csvText) {
        toast("Template was empty — nothing to download.", "error");
        return;
      }
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = "vendor_bulk_template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast("Template downloaded.", "success");
    } catch (e) {
      toast(`Download failed: ${e.message || "unknown error"}`, "error");
    }
  };

  const submit = async () => {
    // BUG-25: block import if pre-flight flagged exact dedup matches.
    if (preview && !preview.can_import) {
      setError(`Cannot import — ${preview.blocking} row(s) duplicate existing vendors. Remove or correct them first.`);
      return;
    }
    setSubmitting(true);
    const res = await postData("t2t/vendors/bulk-import", { rows });
    setSubmitting(false);
    if (res.error) { setError(res.error.message || "Import failed."); return; }
    setResults(res.data?.data || res.data);
    setStep("result");
    if (onDone) onDone();
  };

  // BUG-25: helper for the Dedup column cell. Shows ✓ / ⚠ / ❌ with hover detail.
  const renderDedupCell = (rowNum) => {
    if (previewLoading) return <span style={{ color: "#94A3B8", fontSize: 11 }}>checking…</span>;
    if (!preview)       return <span style={{ color: "#94A3B8" }}>—</span>;
    const r = preview.rows.find((x) => x.row === rowNum);
    if (!r) return <span style={{ color: "#94A3B8" }}>—</span>;
    if (r.errors && r.errors.length > 0) {
      return <span style={{ color: "#94A3B8", fontSize: 10 }} title={r.errors.join(" · ")}>(fix errors first)</span>;
    }
    const d = r.dedup;
    if (!d || d.status === 'clear') {
      return <span style={{ color: "#16A34A", fontSize: 12, fontWeight: 700 }}>✓ No match</span>;
    }
    if (d.status === 'exact') {
      const top = d.top || {};
      const fields = (top.matched_on || []).join("+").toUpperCase();
      return (
        <span style={{ color: "#DC2626", fontSize: 11, fontWeight: 700 }}
              title={`Exact ${fields} match with ${top.vendor_number || top.name}`}>
          ❌ Duplicate ({fields}){top.vendor_number ? `: ${top.vendor_number}` : ""}
        </span>
      );
    }
    if (d.status === 'fuzzy') {
      const top = d.top || {};
      return (
        <span style={{ color: "#D97706", fontSize: 11, fontWeight: 700 }}
              title={`${top.match_score || 0}% match with ${top.vendor_number || top.name}`}>
          ⚠ Possible ({top.match_score || 0}%){top.vendor_number ? `: ${top.vendor_number}` : ""}
        </span>
      );
    }
    return <span style={{ color: "#94A3B8" }}>—</span>;
  };

  return (
    <div className="t2t-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="t2t-modal" style={{ maxWidth: 780, width: "95vw" }}>
        <div className="t2t-modal-head">
          <span className="t2t-modal-title">Bulk Vendor Upload</span>
          <button className="t2t-icon-btn" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="t2t-modal-body">
          {error && (
            <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "#DC2626", borderRadius: 6, fontSize: 13, marginBottom: 12, border: "1px solid #FECACA" }}>
              {error}
            </div>
          )}

          {/* STEP 1: Upload */}
          {step === "upload" && (
            <div style={{ padding: "16px 0 24px" }}>
              {/* BUG-24: explicit expectation-setting banner — each row spawns
                  a vendor-creation REQUEST that still walks through approval. */}
              <div style={{
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: 8,
                padding: "12px 14px",
                fontSize: 12,
                color: "#1E3A8A",
                lineHeight: 1.55,
                marginBottom: 18,
                textAlign: "left",
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                  ℹ️ Each row creates a vendor-creation <em>request</em>, not a vendor record.
                </div>
                <div>
                  Imported rows enter the <strong>same approval workflow</strong> as the
                  public sign-up form and the <em>+ New Vendor Request</em> button —
                  Vendor Initiator → Vendor Checker → Finance Data Controller → MDM Approver.
                  Vendors only appear in Vendor Master <strong>after</strong> their request
                  is fully approved and pushed to SAP.
                </div>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
                  Maximum 200 rows per import. Rows with validation errors are skipped (others go through).
                </div>
              </div>

              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <button
                  className="t2t-btn t2t-btn-outline t2t-btn-sm"
                  style={{ marginRight: 8 }}
                  onClick={downloadTemplate}
                >
                  <i className="bi bi-download me-1" /> Download CSV Template
                </button>
              </div>
              <div style={{
                border: "2px dashed #CBD5E1", borderRadius: 10,
                padding: "40px 20px", cursor: "pointer",
                background: "#F8FAFC",
                textAlign: "center",
              }}
                onClick={() => fileRef.current?.click()}
              >
                <i className="bi bi-cloud-upload" style={{ fontSize: 32, color: "#94A3B8", display: "block", marginBottom: 8 }} />
                <div style={{ fontSize: 14, color: "#64748B" }}>Click to select a CSV file</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                  Required columns: {REQUIRED_COLS.join(", ")} (rest are optional)
                </div>
              </div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleFile} />
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === "preview" && (
            <>
              <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13 }}>
                  <strong>{rows.length} rows</strong> from <span style={{ color: "#64748B" }}>{fileName}</span>
                </div>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => { setStep("upload"); setRows([]); setHeaders([]); setPreview(null); }}>
                  Change file
                </button>
              </div>

              {/* BUG-25: dedup summary banner */}
              {previewLoading && (
                <div style={{ padding: "10px 12px", background: "#F1F5F9", borderRadius: 6, fontSize: 12, color: "#64748B", marginBottom: 10 }}>
                  Checking each row against the existing vendor master for duplicates…
                </div>
              )}
              {!previewLoading && preview && preview.blocking > 0 && (
                <div style={{ padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, fontSize: 12, color: "#991B1B", marginBottom: 10 }}>
                  <strong>{preview.blocking} row(s) cannot be imported</strong> — they duplicate existing vendors on PAN or GSTIN. Remove or correct them, then upload the file again.
                </div>
              )}
              {!previewLoading && preview && preview.blocking === 0 && preview.warning > 0 && (
                <div style={{ padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 12, color: "#92400E", marginBottom: 10 }}>
                  <strong>{preview.warning} row(s) look similar</strong> to existing vendors. They&apos;ll be imported and flagged for the MDM Approver to review during approval.
                </div>
              )}
              {!previewLoading && preview && preview.blocking === 0 && preview.warning === 0 && (
                <div style={{ padding: "10px 12px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 12, color: "#166534", marginBottom: 10 }}>
                  ✓ No duplicates found. All {preview.total} rows are clear to import.
                </div>
              )}

              <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#F1F5F9", position: "sticky", top: 0 }}>
                      <th style={{ padding: "6px 10px", textAlign: "left", whiteSpace: "nowrap" }}>#</th>
                      {ALL_COLS.filter((c) => headers.includes(c)).map((c) => (
                        <th key={c} style={{ padding: "6px 10px", textAlign: "left", whiteSpace: "nowrap" }}>
                          {c}
                          {REQUIRED_COLS.includes(c) && <span style={{ color: "#EF4444" }}> *</span>}
                        </th>
                      ))}
                      {/* BUG-25: Dedup column */}
                      <th style={{ padding: "6px 10px", textAlign: "left", whiteSpace: "nowrap" }}>Dedup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((row, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "5px 10px", color: "#94A3B8" }}>{i + 1}</td>
                        {ALL_COLS.filter((c) => headers.includes(c)).map((c) => (
                          <td key={c} style={{ padding: "5px 10px", whiteSpace: "nowrap" }}>
                            {row[c] || <span style={{ color: "#CBD5E1" }}>—</span>}
                          </td>
                        ))}
                        <td style={{ padding: "5px 10px", whiteSpace: "nowrap" }}>{renderDedupCell(i + 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 50 && (
                  <div style={{ padding: "8px 12px", fontSize: 12, color: "#64748B", textAlign: "center", background: "#F8FAFC" }}>
                    Showing first 50 of {rows.length} rows
                  </div>
                )}
              </div>
            </>
          )}

          {/* STEP 3: Results */}
          {step === "result" && results && (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, padding: 14, background: "#F0FDF4", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#16A34A" }}>{results.success}</div>
                  <div style={{ fontSize: 12, color: "#166534" }}>Created</div>
                </div>
                <div style={{ flex: 1, padding: 14, background: results.failed > 0 ? "#FEF2F2" : "#F8FAFC", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: results.failed > 0 ? "#DC2626" : "#94A3B8" }}>{results.failed}</div>
                  <div style={{ fontSize: 12, color: results.failed > 0 ? "#991B1B" : "#64748B" }}>Failed</div>
                </div>
                <div style={{ flex: 1, padding: 14, background: "#F1F5F9", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#334155" }}>{results.total}</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>Total</div>
                </div>
              </div>
              {results.failed > 0 && (
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #FECACA", borderRadius: 8 }}>
                  {results.results.filter((r) => r.status === "error").map((r) => (
                    <div key={r.row} style={{ padding: "8px 12px", borderBottom: "1px solid #FEE2E2", fontSize: 12 }}>
                      <strong>Row {r.row} — {r.vendor_name}:</strong>{" "}
                      <span style={{ color: "#DC2626" }}>{r.errors?.join("; ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="t2t-modal-foot">
          <button className="t2t-btn t2t-btn-outline" onClick={onClose}>
            {step === "result" ? "Close" : "Cancel"}
          </button>
          {step === "preview" && (
            <button
              className="t2t-btn t2t-btn-primary"
              onClick={submit}
              disabled={submitting || previewLoading || (preview && !preview.can_import)}
              title={preview && !preview.can_import ? "Fix duplicate-vendor rows first" : ""}
            >
              {submitting
                ? "Importing…"
                : previewLoading
                  ? "Checking duplicates…"
                  : (preview && preview.warning > 0
                      ? `Import ${rows.length} (${preview.warning} flagged)`
                      : `Import ${rows.length} Requests`)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

BulkUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDone:  PropTypes.func,
};
